// Actuadores, potencia y lógica: relé, motores (CD y paso a paso), puentes H (L298N y L293D), batería,
// registros de desplazamiento (74HC595 y 74HC165), transistores, MOSFET y diodo.
import { definePart, PITCH } from './registry.js';

const P = PITCH;
const HEADER = 8.5; // altura de un conector macho sobre la cara del PCB (base 2.5 + pin 6)
const TERM_Y = 3.6; // altura de la entrada de cable de una bornera sobre la cara del PCB

// Módulo de relé ---------------------------------------------------------------------------------
export const RELAY = { W: 50, D: 26, PCB_Y: 1.6, HEADER_X: -22.6, TERM_X: 21.2, TERM_PITCH: 5, BODY_X: 4.5 };
RELAY.TOP = RELAY.PCB_Y + 1.6;

definePart({
  type: 'relay-module',
  name: 'Módulo de relé (1 canal, 5 V)',
  category: 'actuadores',
  description: 'Relé SRD-05VDC-SL-C con transistor, LEDs y bornera: su contacto de 10 A une COM con NC en reposo y con NO cuando se activa.',
  keywords: 'rele relay srd-05vdc songle modulo bobina contacto interruptor',
  props: [
    { key: 'trigger', label: 'Disparo (puente H/L)', type: 'select', default: 'low', options: [
      { value: 'low', label: 'En bajo (LOW activa)' },
      { value: 'high', label: 'En alto (HIGH activa)' },
    ] },
  ],
  pins: [
    { name: 'VCC', label: 'VCC (5 V)', kind: 'power', x: RELAY.HEADER_X, y: RELAY.TOP + HEADER, z: -P },
    { name: 'GND', label: 'GND', kind: 'gnd', x: RELAY.HEADER_X, y: RELAY.TOP + HEADER, z: 0 },
    { name: 'IN', label: 'IN (control)', kind: 'signal', x: RELAY.HEADER_X, y: RELAY.TOP + HEADER, z: P },
    ...['NO', 'COM', 'NC'].map((name, i) => ({
      name,
      label: { NO: 'NO (normalmente abierto)', COM: 'COM (común)', NC: 'NC (normalmente cerrado)' }[name],
      kind: 'signal',
      x: RELAY.W / 2 + 0.3,
      y: RELAY.TOP + TERM_Y,
      z: (i - 1) * RELAY.TERM_PITCH,
      dir: [1, 0, 0],
    })),
  ],
  footprint: { w: RELAY.W + 2, d: RELAY.D + 1, h: 19 },
  help: 'Con disparo en bajo (la mayoría de los módulos con optoacoplador) el relé se activa con IN en LOW; en alto, con HIGH. ' +
    'La bobina tarda unos 7 ms en cerrar el contacto y gasta ~70 mA de VCC. Los bornes NO, COM y NC están aislados del Arduino: ' +
    'úsalos para encender un motor o una tira de LEDs con su propia batería.',
});

// Motor de CD ---------------------------------------------------------------------------------------
export const DCMOTOR = {
  R: 10.05, FLAT: 7.55, // lata redonda de 20.1 mm con caras planas a 15.1 mm
  BASE_H: 3, SLEEVE_R: 12.4, SLEEVE_TOP: 17,
  CAN_BOTTOM: 8, CAN_TOP: 33, SHAFT_TOP: 43.5, ROTOR_Y: 40,
  LEAD_X: -28,
};

definePart({
  type: 'dc-motor',
  name: 'Motor de CD (tipo 130)',
  category: 'actuadores',
  description: 'Motor pequeño de 3–6 V con hélice o disco. Gira más rápido con más voltaje y cambia de sentido al invertir la polaridad. Gasta demasiado para un pin: muévelo con transistor, MOSFET, relé o puente H.',
  keywords: 'motor dc cd 130 helice ventilador corriente directa',
  props: [
    { key: 'rotor', label: 'En el eje', type: 'select', default: 'propeller', options: [
      { value: 'propeller', label: 'Hélice' },
      { value: 'disc', label: 'Disco' },
    ] },
    { key: 'brake', label: 'Freno en el eje (100 % = rotor bloqueado)', type: 'slider', min: 0, max: 1, step: 0.05, default: 0, runtime: true, live: true },
  ],
  pins: [
    { name: '+', label: 'Terminal + (cable rojo)', kind: 'power', x: DCMOTOR.LEAD_X, y: 1.0, z: -P / 2, dir: [-1, 0, 0] },
    { name: '-', label: 'Terminal − (cable negro)', kind: 'gnd', x: DCMOTOR.LEAD_X, y: 1.0, z: P / 2, dir: [-1, 0, 0] },
  ],
  footprint: { w: 60, d: 56, h: 46 },
  help: 'Con + más positivo que − gira en sentido horario (visto desde arriba). La velocidad depende del voltaje medio: ' +
    'con PWM (analogWrite) a través de un transistor o de un puente H se regula. Con la hélice gira a unas 8000 rpm con 6 V ' +
    '(sin carga, a unas 11 500); la animación se muestra más lenta para que se aprecie el sentido. Su bobina genera un pico de ' +
    'voltaje al cortarle la corriente: con un transistor o MOSFET pon un diodo 1N4007 en paralelo (franja hacia el +). ' +
    'El freno del eje se opone al giro con un par constante, como fracción del par de arranque con 6 V: más voltaje puede vencerlo; con el 100 % ' +
    'el rotor queda bloqueado y el motor pide la corriente de arranque (≈ 1.6 A con 6 V) hasta que lo sueltes: avisa y en la vida real se quemaría.',
});

// Motorreductor con encoder Hall (JGA25-370) -------------------------------------------------------
// Tabla del fabricante para 12 V: sin carga 46 mA y ~4300 rpm en el rotor, 1 A de atasco; la reducción
// (1:9.6 … 1:171) alarga la caja y baja las rpm del eje (463 … 25 rpm). El encoder Hall da 11 pulsos por
// vuelta del rotor en cada canal (A y B), o sea 44 flancos por vuelta y 44 × reducción por vuelta del eje.
export const GEARMOTOR = {
  RATIOS: [9.6, 21, 35, 78, 171],
  BOX: { 9.6: 17, 21: 19, 35: 21, 78: 23, 171: 25 }, // largo de la caja reductora (mm)
  RPM: { 9.6: 450, 21: 200, 35: 125, 78: 55, 171: 25 }, // eje de salida sin carga a 12 V (el fabricante: 463, 201, 126, 55, 25)
  PPR: 11,
  AXIS_Y: 15, R: 12.2, // altura del eje sobre la mesa y radio de la lata del motor
  CAN_LEN: 32, ENC_LEN: 12, SHAFT: 10, // lata del motor, tapa del encoder y eje de salida
  LEAD_X: -66,
};
const GEAR_PINS = ['M1', 'GND', 'A', 'B', 'VCC', 'M2'];

definePart({
  type: 'gearmotor',
  name: 'Motorreductor con encoder (JGA25-370)',
  category: 'actuadores',
  description: 'Motor de 12 V con caja reductora metálica y encoder Hall de dos canales (A y B en cuadratura, 11 pulsos por vuelta del rotor). Se mueve con un puente H y las vueltas se cuentan con interrupciones.',
  keywords: 'motorreductor motor reductor encoder jga25 ga25 370 hall cuadratura pid velocidad vueltas rpm l298n encoder',
  props: [
    { key: 'ratio', label: 'Reducción', type: 'select', default: 21, options: GEARMOTOR.RATIOS.map((r) => ({
      value: r, label: `1:${r} (≈ ${GEARMOTOR.RPM[r]} rpm)`,
    })) },
    { key: 'brake', label: 'Freno en el eje (100 % = bloqueado)', type: 'slider', min: 0, max: 1, step: 0.05, default: 0, runtime: true, live: true },
  ],
  pins: GEAR_PINS.map((name, i) => ({
    name,
    label: {
      M1: 'M1 (motor, cable rojo)', M2: 'M2 (motor, cable blanco)', GND: 'GND (encoder, cable negro)',
      A: 'A · C1 (encoder, cable amarillo)', B: 'B · C2 (encoder, cable verde)', VCC: 'VCC (encoder 3.3–5 V, cable azul)',
    }[name],
    kind: name === 'GND' ? 'gnd' : name === 'VCC' ? 'power' : 'signal',
    x: GEARMOTOR.LEAD_X,
    y: 1.3,
    z: (i - 2.5) * P,
    dir: [-1, 0, 0],
  })),
  footprint: (props) => {
    const box = GEARMOTOR.BOX[props.ratio] || GEARMOTOR.BOX[21];
    const x0 = GEARMOTOR.LEAD_X - 2;
    const x1 = box + GEARMOTOR.SHAFT + 2;
    return { w: x1 - x0, d: 36, h: 32, x: (x0 + x1) / 2 };
  },
  help: 'Los seis cables son los del motor real: M1 y M2 (motor, 12 V; con M1 más positivo gira en sentido horario visto desde el eje) y el encoder con ' +
    'VCC (3.3–5 V), GND, A y B. Las salidas A y B son de colector abierto con pull-up de 10 kΩ en el módulo (supuesto: los vendedores no lo detallan). ' +
    'B adelanta a A 90° en el giro horario (así cuenta hacia arriba la librería Encoder y el clásico «en el flanco de A, si B está en alto, suma»). Con 11 pulsos por canal y vuelta del rotor, contando los flancos de A y B hay 44 cuentas por vuelta del rotor y ' +
    '44 × reducción por vuelta del eje (924 con 1:21). Conéctalo a un puente H (L298N) con una batería de 12 V; A y B a pines con interrupción (2 y 3 en el Uno) o a la librería Encoder.',
});

// Puente H L298N --------------------------------------------------------------------------------------
export const L298 = { W: 43, D: 43, PCB_Y: 1.6, HDR_X0: 2.2, HDR_Z: 19.6, OUT_Z: [-13.5, -8.5], PWR_X: [-14.5, -9.5, -4.5] };
L298.TOP = L298.PCB_Y + 1.6;
const L298_CTRL = ['ENA', 'IN1', 'IN2', 'IN3', 'IN4', 'ENB'];

definePart({
  type: 'l298n',
  name: 'Puente H L298N (módulo)',
  category: 'logica',
  description: 'Controla dos motores de CD (5–35 V, 2 A por canal): IN1/IN2 dan el sentido del motor A y ENA su velocidad con PWM; IN3/IN4 y ENB, el motor B.',
  keywords: 'l298 l298n puente h h-bridge driver motor dc',
  props: [
    { key: 'ena', label: 'Puente en ENA (motor A siempre habilitado)', type: 'boolean', default: true },
    { key: 'enb', label: 'Puente en ENB (motor B siempre habilitado)', type: 'boolean', default: true },
    { key: 'reg', label: 'Puente 5V-EN (el regulador da +5V desde +12V)', type: 'boolean', default: true },
  ],
  pins: [
    { name: 'OUT1', label: 'OUT1 (motor A)', kind: 'signal', x: -L298.W / 2 - 0.3, y: L298.TOP + TERM_Y, z: L298.OUT_Z[0], dir: [-1, 0, 0] },
    { name: 'OUT2', label: 'OUT2 (motor A)', kind: 'signal', x: -L298.W / 2 - 0.3, y: L298.TOP + TERM_Y, z: L298.OUT_Z[1], dir: [-1, 0, 0] },
    { name: 'OUT3', label: 'OUT3 (motor B)', kind: 'signal', x: L298.W / 2 + 0.3, y: L298.TOP + TERM_Y, z: L298.OUT_Z[1], dir: [1, 0, 0] },
    { name: 'OUT4', label: 'OUT4 (motor B)', kind: 'signal', x: L298.W / 2 + 0.3, y: L298.TOP + TERM_Y, z: L298.OUT_Z[0], dir: [1, 0, 0] },
    { name: '+12V', label: '+12V (alimentación de motores, 5–35 V)', kind: 'power', x: L298.PWR_X[0], y: L298.TOP + TERM_Y, z: L298.D / 2 + 0.3, dir: [0, 0, 1] },
    { name: 'GND', label: 'GND', kind: 'gnd', x: L298.PWR_X[1], y: L298.TOP + TERM_Y, z: L298.D / 2 + 0.3, dir: [0, 0, 1] },
    { name: '+5V', label: '+5V (lógica: salida del regulador o entrada)', kind: 'power', x: L298.PWR_X[2], y: L298.TOP + TERM_Y, z: L298.D / 2 + 0.3, dir: [0, 0, 1] },
    ...L298_CTRL.map((name, i) => ({
      name,
      label: { ENA: 'ENA (habilita motor A, PWM)', ENB: 'ENB (habilita motor B, PWM)' }[name] || `${name} (sentido)`,
      kind: 'signal',
      x: L298.HDR_X0 + i * P,
      y: L298.TOP + HEADER,
      z: L298.HDR_Z,
    })),
  ],
  buses: (props) => [...(props.ena ? [['ENA', '+5V']] : []), ...(props.enb ? [['ENB', '+5V']] : [])],
  footprint: { w: L298.W + 2, d: L298.D + 2, h: 28 },
  help: 'Motor A entre OUT1 y OUT2: IN1=HIGH e IN2=LOW gira en un sentido, al revés en el otro, iguales = freno. ' +
    'Con ENA en LOW las salidas quedan sueltas y el motor se detiene por inercia. Las salidas pierden ~2 V. ' +
    'Quita el puente de ENA/ENB para regular la velocidad con PWM. Con el puente 5V-EN y más de 7 V en +12V, el pin +5V entrega 5 V; ' +
    'sin el puente, alimenta +5V desde el Arduino. Une siempre GND del módulo con GND del Arduino.',
});

// Batería --------------------------------------------------------------------------------------------
// v: fuerza electromotriz (V); rint: resistencia interna nueva a temperatura ambiente (Ω). Una AA alcalina
// tiene 150–300 mΩ (Energizer E91; ~0.15 Ω a 25 °C), la de 9 V 1.7 Ω (Duracell MN1604, a 1 kHz) y una 18650
// hasta 35 mΩ (Samsung INR18650-35E, a 1 kHz). Los portapilas suman las de sus pilas en serie.
export const BATTERIES = {
  '3.7': { label: 'Pila 18650 (3.7 V)', w: 77, d: 21, h: 19.5, v: 3.7, rint: 0.035, rechargeable: true },
  '4.5': { label: 'Portapilas 3×AA (4.5 V)', w: 58, d: 47, h: 15.5, v: 4.5, rint: 0.45 },
  '6': { label: 'Portapilas 4×AA (6 V)', w: 58, d: 62, h: 15.5, v: 6, rint: 0.6 },
  '9': { label: 'Batería de 9 V', w: 48.5, d: 26.5, h: 17.5, v: 9, rint: 1.7 },
  '12': { label: 'Portapilas 8×AA (12 V)', w: 58, d: 62, h: 31, v: 12, rint: 1.2 },
};
const BATTERY_ORDER = ['3.7', '4.5', '6', '9', '12'];

export function batteryType(props) {
  return BATTERIES[String(props.voltage)] || BATTERIES['9'];
}

/** Punta del cable rojo (+) y negro (−). */
export function batteryLeadX(t) {
  return t.w / 2 + 16;
}

definePart({
  type: 'battery',
  name: 'Batería',
  category: 'logica',
  description: 'Pilas AA, 18650 o batería de 9 V con su resistencia interna: alimenta motores, módulos o el Arduino por VIN, y se pueden poner en serie.',
  keywords: 'bateria pila portapilas 9v aa 18650 fuente alimentacion',
  props: [
    { key: 'voltage', label: 'Voltaje', type: 'select', default: '9', options: BATTERY_ORDER.map((value) => ({ value, label: BATTERIES[value].label })) },
  ],
  variants: ['9', '6', '4.5', '12', '3.7'].map((v) => ({ label: BATTERIES[v].label, props: { voltage: v } })),
  pins: (props) => {
    const t = batteryType(props);
    return [
      { name: '+', label: '+ (cable rojo)', kind: 'power', x: batteryLeadX(t), y: 1.0, z: -P / 2, dir: [1, 0, 0] },
      { name: '-', label: '− (cable negro)', kind: 'gnd', x: batteryLeadX(t), y: 1.0, z: P / 2, dir: [1, 0, 0] },
    ];
  },
  footprint: (props) => {
    const t = batteryType(props);
    return { w: t.w + 18, d: t.d, h: t.h, x: 9 };
  },
  help: 'Da su voltaje entre + y − a través de su resistencia interna (0.45–1.7 Ω en pilas alcalinas, ~35 mΩ en una 18650): con más ' +
    'corriente su voltaje baja. Se pueden poner en serie (el + de una al − de la otra). Su − no es el GND del Arduino: si lo que ' +
    'alimenta intercambia señales con la placa, une el − con un GND del Arduino (tierra común); un circuito aparte funciona solo. ' +
    'Para alimentar el Arduino, su + va a VIN (7–12 V) y su − a GND. No se modela la descarga: el voltaje no baja con el tiempo.',
});

// Motor paso a paso 28BYJ-48 con placa ULN2003 ------------------------------------------------------
export const BYJ = {
  W: 35, D: 32, PCB_Y: 1.6,
  IN_X: -15.6, PWR_Z: 13.6, PWR_X: 4.2,
  MOTOR_X: 4, MOTOR_Z: -38, R: 14, H: 19, SHAFT_OFF: 8,
};
BYJ.TOP = BYJ.PCB_Y + 1.6;

definePart({
  type: 'stepper-28byj48',
  name: 'Motor paso a paso 28BYJ-48 con ULN2003',
  category: 'actuadores',
  description: 'Motor unipolar de 5 V con reductora (≈ 1:64) y su placa ULN2003 con LEDs A–D: 2048 pasos completos o 4096 medios pasos por vuelta.',
  keywords: 'paso a paso stepper 28byj-48 28byj48 uln2003 unipolar accelstepper',
  pins: [
    ...['IN1', 'IN2', 'IN3', 'IN4'].map((name, i) => ({
      name, label: `${name} (bobina ${'ABCD'[i]})`, kind: 'signal', x: BYJ.IN_X, y: BYJ.TOP + HEADER, z: (i - 1.5) * P,
    })),
    { name: 'GND', label: '− (GND)', kind: 'gnd', x: BYJ.PWR_X, y: BYJ.TOP + HEADER, z: BYJ.PWR_Z },
    { name: 'VCC', label: '+ (5 V)', kind: 'power', x: BYJ.PWR_X + P, y: BYJ.TOP + HEADER, z: BYJ.PWR_Z },
  ],
  footprint: { w: 44, d: 68, h: 31, x: 3.5, z: -18 },
  help: 'Librería Stepper: <code>Stepper motor(2048, 8, 10, 9, 11)</code> con IN1–IN4 en los pines 8, 9, 10 y 11 (el orden 8, 10, 9, 11 es a propósito). ' +
    'AccelStepper: <code>AccelStepper motor(AccelStepper::HALF4WIRE, 8, 10, 9, 11)</code> usa medios pasos (4096 por vuelta). ' +
    'Más de ~1700 medios pasos por segundo (≈ 25 rpm) y el motor pierde pasos y se queda vibrando, como el real.',
});

// Motor NEMA 17 con driver A4988 ------------------------------------------------------------------------
export const NEMA = {
  S: 42.3, H: 40, MX: 24,
  CW: 30, CD: 36, CX: -18, PCB_Y: 1.6,
  HDR_X: -30.5, TERM_X: -25.5,
};
NEMA.TOP = NEMA.PCB_Y + 1.6;
export const NEMA_MICRO = [1, 2, 4, 8, 16];

definePart({
  type: 'stepper-nema17',
  name: 'Motor NEMA 17 con driver A4988',
  category: 'actuadores',
  description: 'Motor bipolar de 200 pasos por vuelta (1.8°) con driver A4988: cada pulso en STEP avanza un (micro)paso hacia donde indica DIR. EN en LOW lo habilita.',
  keywords: 'paso a paso stepper nema17 nema 17 a4988 driver bipolar step dir accelstepper',
  props: [
    { key: 'micro', label: 'Micropasos (interruptores MS1–MS3)', type: 'select', default: '1', runtime: true,
      options: NEMA_MICRO.map((m) => ({ value: String(m), label: m === 1 ? 'Paso completo' : `1/${m}` })) },
  ],
  pins: [
    ...['EN', 'STEP', 'DIR', 'VDD', 'GND'].map((name, i) => ({
      name,
      label: { EN: 'EN (habilitar, activo en LOW)', STEP: 'STEP (un pulso = un paso)', DIR: 'DIR (sentido)', VDD: 'VDD (lógica 3–5.5 V)', GND: 'GND' }[name],
      kind: name === 'GND' ? 'gnd' : name === 'VDD' ? 'power' : 'signal',
      x: NEMA.HDR_X, y: NEMA.TOP + HEADER, z: (i - 2) * P,
    })),
    { name: 'VMOT', label: 'VMOT (motor, 8–35 V)', kind: 'power', x: NEMA.TERM_X, y: NEMA.TOP + TERM_Y, z: NEMA.CD / 2 + 0.3, dir: [0, 0, 1] },
    { name: 'GND.2', label: 'GND (motor)', kind: 'gnd', x: NEMA.TERM_X + 5, y: NEMA.TOP + TERM_Y, z: NEMA.CD / 2 + 0.3, dir: [0, 0, 1] },
  ],
  buses: [['GND', 'GND.2']],
  footprint: { w: 80, d: 44, h: 66, x: 6 },
  help: 'Con DIR en HIGH gira en sentido horario (visto desde el eje). Con micropasos de 1/16 una vuelta son 3200 pulsos. ' +
    'Necesita 8–35 V en VMOT (una batería de 9 o 12 V) y 5 V en VDD; GND común con el Arduino. ' +
    'AccelStepper: <code>AccelStepper motor(AccelStepper::DRIVER, pinStep, pinDir)</code>.',
});

// Registro de desplazamiento 74HC595 (DIP-16) --------------------------------------------------------
// Pines según la hoja de datos: 1–8 abajo (de izquierda a derecha) y 9–16 arriba (de derecha a izquierda).
export const HC595_PINS = ['QB', 'QC', 'QD', 'QE', 'QF', 'QG', 'QH', 'GND', 'QHS', 'SRCLR', 'SRCLK', 'RCLK', 'OE', 'SER', 'QA', 'VCC'];
const HC595_LABELS = {
  QHS: "QH' (salida serie, para encadenar)", SRCLR: 'SRCLR (borrar, activo en LOW)', SRCLK: 'SRCLK (reloj de desplazamiento)',
  RCLK: 'RCLK (reloj de salida, «latch»)', OE: 'OE (habilitar salidas, activo en LOW)', SER: 'SER (dato en serie)', VCC: 'VCC (2–6 V)',
};

/** Pines de un DIP de n patas (fila de 0.3"), pin 1 abajo a la izquierda. */
export function dipPins(names, labels = {}, kinds = {}) {
  const n = names.length;
  const half = n / 2;
  return names.map((name, i) => {
    const bottom = i < half;
    const k = bottom ? i : n - 1 - i;
    return {
      name,
      label: `${i + 1} · ${labels[name] || name}`,
      kind: kinds[name] || (name === 'GND' ? 'gnd' : name === 'VCC' || name === 'VSS' || name === 'VS' ? 'power' : 'signal'),
      x: (k - (half - 1) / 2) * P,
      y: 0,
      z: bottom ? 1.5 * P : -1.5 * P,
      insert: true,
    };
  });
}

definePart({
  type: '74hc595',
  name: 'Registro de desplazamiento 74HC595',
  category: 'logica',
  description: 'Convierte datos en serie (SER y SRCLK) en 8 salidas que cambian juntas con RCLK. Se usa con shiftOut() y se encadena por QH′ para tener 16, 24… salidas.',
  keywords: '74hc595 595 shift register registro desplazamiento shiftout salidas expansor sn74hc595',
  insertable: true,
  pins: dipPins(HC595_PINS, HC595_LABELS),
  footprint: { w: 20.5, d: 9, h: 6 },
  help: 'Conexión típica: VCC y SRCLR a 5 V, GND y OE a GND, SER al pin de datos, SRCLK al reloj y RCLK al «latch» ' +
    '(<code>shiftOut(datos, reloj, MSBFIRST, valor)</code> y un pulso en RCLK). Cada salida da ~20 mA como máximo y el chip 70 mA en total.',
});

// Puente H L293D (DIP-16) ------------------------------------------------------------------------------
export const L293_PINS = ['EN12', 'IN1', 'OUT1', 'GND', 'GND.2', 'OUT2', 'IN2', 'VS', 'EN34', 'IN3', 'OUT3', 'GND.3', 'GND.4', 'OUT4', 'IN4', 'VSS'];
const L293_LABELS = {
  EN12: '1,2EN (habilita OUT1 y OUT2, PWM)', IN1: '1A (entrada de OUT1)', OUT1: '1Y (salida 1)', GND: 'GND', 'GND.2': 'GND',
  OUT2: '2Y (salida 2)', IN2: '2A (entrada de OUT2)', VS: 'VCC2 (motores, 4.5–36 V)', EN34: '3,4EN (habilita OUT3 y OUT4, PWM)',
  IN3: '3A (entrada de OUT3)', OUT3: '3Y (salida 3)', 'GND.3': 'GND', 'GND.4': 'GND', OUT4: '4Y (salida 4)', IN4: '4A (entrada de OUT4)',
  VSS: 'VCC1 (lógica, 5 V)',
};

definePart({
  type: 'l293d',
  name: 'Puente H L293D',
  category: 'logica',
  description: 'Dos puentes H en un chip DIP-16 (600 mA por canal, con diodos de protección): mueve dos motores de CD pequeños en ambos sentidos.',
  keywords: 'l293d l293 puente h h-bridge driver motor dip16 sn754410',
  insertable: true,
  pins: dipPins(L293_PINS, L293_LABELS, { VS: 'power', VSS: 'power', 'GND.2': 'gnd', 'GND.3': 'gnd', 'GND.4': 'gnd' }),
  buses: [['GND', 'GND.2', 'GND.3', 'GND.4']],
  footprint: { w: 20.5, d: 9, h: 6 },
  help: 'Motor 1 entre OUT1 (pin 3) y OUT2 (pin 6): IN1 e IN2 dan el sentido y EN12 (pin 1) la velocidad con PWM. ' +
    'VSS (pin 16) a 5 V, VS (pin 8) a la batería de los motores y las cuatro patas GND del centro a tierra común con el Arduino. ' +
    'Cada salida pierde ~1.3 V a 0.6 A.',
});

// Registro de entrada 74HC165 (DIP-16) --------------------------------------------------------------------
export const HC165_PINS = ['SHLD', 'CLK', 'E', 'F', 'G', 'H', 'QHN', 'GND', 'QH', 'SER', 'A', 'B', 'C', 'D', 'CLKINH', 'VCC'];
const HC165_LABELS = {
  SHLD: 'SH/LD (LOW copia las entradas)', CLK: 'CLK (reloj)', QHN: '/QH (salida serie invertida)', QH: 'QH (salida serie)',
  SER: 'SER (entrada serie, para encadenar)', CLKINH: 'CLK INH (inhibe el reloj: a GND)', VCC: 'VCC (2–6 V)',
};

definePart({
  type: '74hc165',
  name: 'Registro de entrada 74HC165',
  category: 'logica',
  description: 'Lee 8 entradas (botones, interruptores) con 3 pines: SH/LD en LOW copia A–H y cada pulso de CLK las saca una por una por QH, empezando por H.',
  keywords: '74hc165 165 shift register registro entradas botones shiftin expansor paralelo serie',
  insertable: true,
  pins: dipPins(HC165_PINS, HC165_LABELS),
  footprint: { w: 20.5, d: 9, h: 6 },
  help: 'Conexión típica: VCC a 5 V, GND y CLK INH a GND, SH/LD y CLK a dos salidas y QH a una entrada del Arduino. ' +
    'Para leer: pulso en LOW en SH/LD; luego lee QH (entrada H) y da un pulso en CLK para cada una de las 7 siguientes. ' +
    'Ninguna entrada debe quedar al aire: usa resistencias a GND (o a 5 V) con los botones.',
});

// Transistores, MOSFET y diodo -------------------------------------------------------------------
const to92Pins = (labels) => ['E', 'B', 'C'].map((name, i) => ({
  name, label: labels[name], kind: 'signal', x: (i - 1) * P, y: 0, z: 0, insert: true,
}));

definePart({
  type: 'npn-transistor',
  name: 'Transistor NPN 2N2222',
  category: 'basicos',
  description: 'Interruptor de hasta 600 mA: una corriente pequeña de la base al emisor (con una resistencia de ~1 kΩ desde el pin) deja pasar una ~150 veces mayor del colector al emisor.',
  keywords: 'transistor npn 2n2222 pn2222 bjt interruptor amplificador to-92',
  insertable: true,
  pins: to92Pins({ E: 'E (emisor, a GND)', B: 'B (base)', C: 'C (colector, a la carga)' }),
  footprint: { w: 7.6, d: 5, h: 9 },
  help: 'Vista desde la cara plana con las patas hacia abajo: E, B, C. Para usarlo como interruptor pon la carga entre +V y el colector, ' +
    'el emisor a GND y una resistencia de 1 kΩ entre el pin y la base. Con una resistencia de base muy grande no satura y la carga recibe menos corriente.',
});

definePart({
  type: 'pnp-transistor',
  name: 'Transistor PNP 2N2907',
  category: 'basicos',
  description: 'El complemento del 2N2222: conduce del emisor al colector cuando la base está ~0.7 V por debajo del emisor. Sirve para conmutar la alimentación (+) de una carga.',
  keywords: 'transistor pnp 2n2907 pn2907 bjt interruptor lado alto to-92',
  insertable: true,
  pins: to92Pins({ E: 'E (emisor, a +V)', B: 'B (base)', C: 'C (colector, a la carga)' }),
  footprint: { w: 7.6, d: 5, h: 9 },
  help: 'Vista desde la cara plana con las patas hacia abajo: E, B, C. El emisor va a +5 V y la carga entre el colector y GND; ' +
    'con la base a LOW (por una resistencia de 1 kΩ) conduce y con HIGH se apaga: la lógica es inversa a la del NPN.',
});

definePart({
  type: 'mosfet-n',
  name: 'MOSFET de canal N IRLZ44N',
  category: 'basicos',
  description: 'Transistor de potencia de nivel lógico (TO-220): con 5 V en la compuerta conduce varios amperes entre drenador y fuente con solo 0.025 Ω.',
  keywords: 'mosfet irlz44n canal n nivel logico potencia motor to-220 transistor',
  insertable: true,
  pins: ['G', 'D', 'S'].map((name, i) => ({
    name,
    label: { G: 'G (compuerta)', D: 'D (drenador, a la carga)', S: 'S (fuente, a GND)' }[name],
    kind: name === 'S' ? 'gnd' : 'signal',
    x: (i - 1) * P, y: 0, z: 0, insert: true,
  })),
  footprint: { w: 10.5, d: 5, h: 17 },
  help: 'Vista de frente (letras hacia ti): G, D, S. Para un motor: + de la batería al motor, el motor al drenador y la fuente a GND. ' +
    'Pon una resistencia de 10 kΩ de la compuerta a GND (para que no quede al aire) y un diodo 1N4007 en paralelo con el motor, con la franja hacia el +. ' +
    'Tiene un diodo interno de fuente a drenador: si lo conectas al revés, conduce siempre.',
});

definePart({
  type: 'diode',
  name: 'Diodo rectificador 1N4007',
  category: 'basicos',
  description: 'Deja pasar la corriente solo del ánodo al cátodo (la franja), con una caída de ~0.7 V y hasta 1 A. Protege contra polaridad invertida y los picos de motores y relés.',
  keywords: 'diodo 1n4007 1n4001 rectificador proteccion flyback rueda libre',
  insertable: true,
  pins: [
    { name: 'A', label: 'Ánodo (+)', kind: 'signal', x: -2 * P, y: 0, z: 0, insert: true },
    { name: 'K', label: 'Cátodo (franja)', kind: 'signal', x: 2 * P, y: 0, z: 0, insert: true },
  ],
  footprint: { w: 12, d: 3.5, h: 5 },
});
